import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/guides/palworld-sleeve-and-card-set-vol-1-cards";
const TITLE = "Palworld Sleeve & Card Set Vol 1 Checklist & Prices";
const DESCRIPTION =
  "Official sleeve set contents, configuration checklists and secondary market tracker analytics for the Palworld TCG Sleeve & Card Set Vol. 1 covers.";

const FAQ: FaqItem[] = [
  {
    q: "What comes in the Palworld Sleeve & Card Set Vol. 1?",
    a: "The set pairs a run of textured matte card sleeves with a dedicated alternate-art promo insert, so it is bought both as playable protection and as a sealed collectible.",
  },
  {
    q: "Is the Vol. 1 promo card only available in the sleeve set?",
    a: "The promo insert is exclusive to the accessory set, which is why sealed copies hold value even after the sleeves themselves are widely stocked.",
  },
  {
    q: "Where do the sleeve set prices on this page come from?",
    a: "Completed-sale figures come from TCGplayer sold history; anything shown from other marketplaces is an active asking listing, never a sale.",
  },
  {
    q: "Should I open the set or keep it sealed?",
    a: "If you play, open it — sleeves protect the singles you already own. If you collect, sealed accessory sets with an exclusive promo tend to be the scarcer half of the print run over time.",
  },
];

export const Route = createFileRoute("/guides/palworld-sleeve-and-card-set-vol-1-cards")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld Guides", "/guides"],
        ["Sleeve & Card Set Vol. 1", "/guides/palworld-sleeve-and-card-set-vol-1-cards"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: SleeveSetVol1Page,
});

function SleeveSetVol1Page() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · accessories</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Official Palworld Sleeve &amp; Card Set Volume 1 Value Tracker
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Sleeve &amp; Card Set Vol. 1 is an official accessory bundle: a run of textured matte sleeves plus an
          exclusive alternate-art promo insert. Because the promo only ships inside the set, sealed copies price as a
          collectible rather than as plain card protection.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Supply distribution and verified market values</h2>
        <p>
          Accessory runs are allocated in smaller waves than booster product, so secondary availability tightens first
          and then settles once restocks land. We read completed TCGplayer sold history for this set instead of asking
          prices, which keeps a single optimistic listing from dragging the tracked value upward.
        </p>
        <p>
          Buying more than one — for a playgroup, or to keep one sealed — is usually cheaper as a single consolidated
          order. Check current entry conditions on our{" "}
          <Link to="/marketplace" className="text-primary hover:underline">
            Palworld TCG bulk orders platform
          </Link>
          .
        </p>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Sleeve set contents checklist</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Textured sleeves:</strong> premium matte card protection covers sized for
            standard Palworld TCG singles.
          </li>
          <li>
            <strong className="text-foreground">Promo insert:</strong> dedicated alternate-art parallel foil card,
            exclusive to the accessory set.
          </li>
          <li>
            <strong className="text-foreground">Packaging:</strong> sealed outer box — the condition grade collectors
            price on.
          </li>
        </ul>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Keep tracking</h2>
        <ul className="mt-3 space-y-2 text-sm">
          <li>
            <Link to="/guides/palworld-tcg-sleeve-set-vol-2" className="text-primary hover:underline">
              Sleeve &amp; Card Set Vol. 2 guide
            </Link>
          </li>
          <li>
            <Link to="/tcg-sold-prices" className="text-primary hover:underline">
              Palworld TCG last sold prices index
            </Link>
          </li>
          <li>
            <Link to="/tcg-price-index" className="text-primary hover:underline">
              Palworld TCG price index market trends
            </Link>
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Sleeve & Card Set Vol. 1 FAQ" />
    </div>
  );
}
